/**
 * Proposes which Aloha tenant a Stripe customer belongs to. Proposes — never decides.
 *
 * The order is the one that works on this data: a name match is strongest, a shared email domain
 * next. Domain matching does most of the real work, because many Stripe billing emails are
 * accounts-payable mailboxes (`payable@`, `comptabilite@`, `achats@`) that share nothing with any
 * app user's address except the company domain.
 *
 * Shared by the link drawer (tenant → pick a customer) and the unlinked-customers panel
 * (customer → pick a tenant), so the two can never rank the same pair differently.
 */

const RANK = { strong: 3, medium: 2, weak: 1 };

export function matchConfidence(customer, tenantName) {
  const tenant = (tenantName || '').toLowerCase();
  const name = (customer.name || '').toLowerCase();

  if (name && tenant && (name.includes(tenant) || tenant.includes(name))) {
    return 'strong';
  }

  const domain = (customer.email || '').toLowerCase().split('@')[1];
  if (domain && tenant) {
    const stem = domain.split('.')[0];
    if (stem.length > 3 && tenant.replace(/\s+/g, '').includes(stem)) return 'medium';
  }

  return 'weak';
}

export function rankConfidence(confidence) {
  return RANK[confidence] || 0;
}

/**
 * The single best tenant for a customer, or null when nothing rises above "weak" — a weak match is
 * every tenant, so proposing one would be a guess dressed up as a suggestion.
 */
export function bestTenantFor(customer, tenants) {
  let best = null;
  let bestRank = rankConfidence('weak');

  for (const tenant of tenants) {
    const rank = rankConfidence(matchConfidence(customer, tenant.tenantName));
    if (rank > bestRank) {
      best = tenant;
      bestRank = rank;
    }
  }

  return best;
}
